import { useEffect, useState } from "react";
import api from "../api";
import Select from "react-select";
import "./AdminEmployeeReviewDetail.css";

/* MASTER POINT LIST */
const REVIEW_POINTS = [
  "Behavior",
  "Teamwork",
  "Dedication",
  "Punctuality",
  "Accuracy",
  "Working Pattern",
  "Truthfulness",
  "System Adoption",
  "Software Skills",
  "Client Communication",
  "Internal Communication",
  "Work Responsibility",
  "Work Accountability",
  "Language Skills",
  "Innovation",
];

const POINT_OPTIONS = REVIEW_POINTS.map((p) => ({
  value: p,
  label: p,
}));

export default function AdminEmployeeReviewDetail() {
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [search, setSearch] = useState("");
  const [openEmployee, setOpenEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const emptyForm = {
    employeeId: "",
    positivePoints: {},
    improvementAreas: {},
    managementSuggestion: "",
  };

  const [form, setForm] = useState(emptyForm);

  /* LOAD DATA */
  const loadAll = async () => {
    try {
      const u = await api.get("/users");
      const r = await api.get("/employee-review/admin/all");
      setUsers(u.data || []);
      setReviews(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  /* POINT TYPE */
  const setPointType = (point, type) => {
    const pos = { ...form.positivePoints };
    const imp = { ...form.improvementAreas };

    delete pos[point];
    delete imp[point];

    if (type === "positive") pos[point] = "";
    if (type === "improvement") imp[point] = "";

    setForm({ ...form, positivePoints: pos, improvementAreas: imp });
  };

  /* DESCRIPTION */
  const setDescription = (point, type, value) => {
    setForm({
      ...form,
      [type]: { ...form[type], [point]: value },
    });
  };

  /* SUBMIT */
  const submit = async () => {
    if (!form.employeeId) return alert("Select employee");
    if (selectedPoints.length === 0)
      return alert("Select at least one review point");

    setSubmitting(true);
    try {
      await api.post("/employee-review", form);
      alert("✅ Review Assigned Successfully");
      setForm(emptyForm);
      setSelectedPoints([]);
      loadAll();
    } finally {
      setSubmitting(false);
    }
  };

  /* MARK ACHIEVED */
  const markAchieved = async (id) => {
    await api.put(`/employee-review/${id}/status`, { status: "ACHIEVED" });
    loadAll();
  };

  /* SEARCH FILTER */
  const filteredReviews = reviews.filter((r) =>
    r.employeeId?.name?.toLowerCase().includes(search.toLowerCase())
  );

  /* GROUP BY EMPLOYEE */
  const grouped = filteredReviews.reduce((acc, r) => {
    const id = r.employeeId?._id;
    if (!id) return acc;

    if (!acc[id]) {
      acc[id] = { employee: r.employeeId, reviews: [] };
    }
    acc[id].reviews.push(r);
    return acc;
  }, {});

  if (loading) return <div>Loading...</div>;

  return (
    <div className="software-page">
      <div className="license-card">
        <h2>Employee Review Form</h2>

        {/* EMPLOYEE */}
        <div className="lc-row">
          <span>Employee</span>
          <select
            value={form.employeeId}
            onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
          >
            <option value="">Select Employee</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        {/* REVIEW POINTS */}
        <div className="lc-row">
          <span>Review Points</span>
          <Select
            isMulti
            options={POINT_OPTIONS}
            value={selectedPoints}
            onChange={(vals) => setSelectedPoints(vals || [])}
            placeholder="Select review points..."
            classNamePrefix="rs"
            isClearable={false}
          />
        </div>

        {/* POINT DETAILS */}
        {selectedPoints.map((p) => {
          const point = p.value;
          const isPos = point in form.positivePoints;
          const isImp = point in form.improvementAreas;

          return (
            <div key={point} className="review-point">
              <strong>{point}</strong>

              <div className="review-radio">
                <label>
                  <input
                    type="radio"
                    checked={isPos}
                    onChange={() => setPointType(point, "positive")}
                  />
                  Positive
                </label>

                <label>
                  <input
                    type="radio"
                    checked={isImp}
                    onChange={() => setPointType(point, "improvement")}
                  />
                  Improvement
                </label>
              </div>

              {(isPos || isImp) && (
                <textarea
                  placeholder="Write details"
                  value={
                    isPos
                      ? form.positivePoints[point]
                      : form.improvementAreas[point]
                  }
                  onChange={(e) =>
                    setDescription(
                      point,
                      isPos ? "positivePoints" : "improvementAreas",
                      e.target.value
                    )
                  }
                />
              )}
            </div>
          );
        })}

        <h4>Suggestio from Management</h4>
        <textarea
          className="management-textarea" // ✅ ADD THIS
          value={form.managementSuggestion}
          onChange={(e) =>
            setForm({ ...form, managementSuggestion: e.target.value })
          }
        />

        <button className="primary-btn" disabled={submitting} onClick={submit}>
          {submitting ? "Submitting..." : "Submit Review"}
        </button>

        <div className="section-divider" />

        {/* SEARCH */}
        <input
          className="search-input"
          placeholder="🔍 Search employee..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* EMPLOYEE WISE REVIEWS */}
        <h3>Employee Wise Reviews</h3>

        {Object.values(grouped).map(({ employee, reviews }) => {
          const achievedCount = reviews.filter(
            (r) => r.status === "ACHIEVED"
          ).length;
          const repliedCount = reviews.filter(
            (r) => r.status === "REPLIED"
          ).length;
          const pendingCount = reviews.filter(
            (r) => r.status === "PENDING"
          ).length;

          const positiveCount = reviews.reduce(
            (sum, r) => sum + Object.keys(r.positivePoints || {}).length,
            0
          );

          const improvementCount = reviews.reduce(
            (sum, r) => sum + Object.keys(r.improvementAreas || {}).length,
            0
          );

          const open = openEmployee === employee._id;

          return (
            <div key={employee._id} className="employee-accordion">
              {/* HEADER */}
              <div
                className="employee-header"
                onClick={() => setOpenEmployee(open ? null : employee._id)}
              >
                <b>{employee.name}</b>

                <div className="count-group">
                  <span className="count-pill positive">
                    ⭐ Positive : {positiveCount}
                  </span>
                  <span className="count-pill improvement">
                    ⚠️ Improvement : {improvementCount}
                  </span>
                  <span className="count-pill achieved">
                    🏆 Achieved : {achievedCount}
                  </span>

                  <span className="count-pill pending">
                    ⏳ Pending : {pendingCount}
                  </span>
                  <span className="arrow">{open ? "▲" : "▼"}</span>
                </div>
              </div>

              {/* BODY */}
              {open && (
                <div className="employee-body">
                  {reviews.map((r) => (
                    <div key={r._id} className="review-card">
                      <span
                        className={`status-badge ${
                          r.status === "ACHIEVED"
                            ? "status-achieved"
                            : r.status === "REPLIED"
                            ? "status-replied"
                            : "status-pending"
                        }`}
                      >
                        {r.status === "ACHIEVED"
                          ? "🏆 ACHIEVED"
                          : r.status === "REPLIED"
                          ? "📝 REPLIED"
                          : "⏳ PENDING"}
                      </span>

                      <h4>⭐ Positive</h4>
                      <ul>
                        {Object.entries(r.positivePoints || {}).map(
                          ([p, d]) => (
                            <li key={p}>
                              <b>{p}</b> {d && `– ${d}`}
                            </li>
                          )
                        )}
                      </ul>

                      <h4>⚠️ Improvement</h4>
                      <ul>
                        {Object.entries(r.improvementAreas || {}).map(
                          ([p, d]) => (
                            <li key={p}>
                              <b>{p}</b> {d && `– ${d}`}
                            </li>
                          )
                        )}
                      </ul>
                      {/* MANAGEMENT SUGGESTION */}
                      {r.managementSuggestion && (
                        <div className="management-box">
                          <div className="management-title">
                            💡 Suggestion from Management
                          </div>
                          <div className="management-text">
                            {r.managementSuggestion}
                          </div>
                        </div>
                      )}
                      {/* EMPLOYEE REPLY */}
                      {r.employeeOverallReply && (
                        <div className="employee-reply-box">
                          <div className="employee-reply-title">
                            📝 Employee Reply
                          </div>
                          <div className="employee-reply-text">
                            {r.employeeOverallReply}
                          </div>
                        </div>
                      )}

                      {r.employeeOverallReply && r.status !== "ACHIEVED" && (
                        <button
                          className="primary-btn small"
                          onClick={() => markAchieved(r._id)}
                        >
                          🏆 Mark Achieved
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
